/**
 * What the Tools page says when the API refuses to save a binding (#1815). `request()` throws the reason the server
 * named as the error's message (`ceo_bind_only`) and carries the status beside it, so the copy is chosen by the
 * reason, never by the status: two 403s can mean different things.
 *
 * The reasons are the ones the bind, edit and delete routes return today. One that is not listed (a reason added
 * later, or a failure with no reason) has no copy here: the screen falls back to its generic message for the kind of
 * tool being saved, and never shows the raw reason.
 */
const REFUSALS: ReadonlyMap<string, string> = new Map([
    [
        'ceo_bind_only',
        'Only the owner of this enterprise, or a member the owner has allowed, can change an enterprise-wide tool.',
    ],
    [
        'must_gate_ceo_only',
        'Only the owner of this enterprise, or a member the owner has allowed, can change which tools need approval.',
    ],
    ['invalid_layer', 'Choose where this tool applies: the whole enterprise, a team, a member, a role or a task.'],
    ['tool_type_required', 'Give this tool a type, its prefix such as github, and choose where it applies.'],
    [
        'invalid_tool_type',
        'A tool type starts with a letter and uses only lower-case letters, digits, dashes and underscores, up to 32 characters.',
    ],
    ['layer_mismatch', 'An enterprise-wide tool can only be bound to the enterprise you are signed in to.'],
    ['binding_exists', 'This tool is already bound here. Edit that binding instead.'],
    ['tool_binding_not_found', 'This tool is no longer bound. Reload the page to see what is.'],
    ['mcp_url_required', 'Enter the MCP server’s URL.'],
    ['not_mcp_url', 'That URL is not an MCP server. Enter the server’s MCP endpoint.'],
    ['tool_image_invalid', 'The image must be a PNG, JPEG, WebP or GIF of up to 5 MB.'],
    ['slots_invalid', 'List the slots this tool fills as keys, like codehost, separated by commas.'],
    ['too_many_slots', 'A tool fills at most 16 slots.'],
    [
        'tool_slot_key_invalid',
        'A slot key starts with a lowercase letter, then up to 31 lowercase letters, digits, - or _.',
    ],
    ['tool_slot_key_reserved', 'A slot cannot take the name of one of the OS’s own tool families.'],
    [
        'header_invalid',
        'That header name cannot carry a credential. Use a header name such as x-api-key, made of letters, digits and dashes, and not one the request itself sets, like host, accept or content-type.',
    ],
    ['scheme_invalid', 'Choose how it is sent: after the word Bearer, or on its own (Raw).'],
    [
        'header_not_supported',
        'An OAuth sign-in always sends its token in Authorization, so it takes no header or scheme.',
    ],
])

/**
 * The wider grants a lower layer's Computer-Use sites are measured against, in words. The task layer is never a wider
 * grant, so it has no entry: a code naming it is not one the portal can read.
 */
const WIDER_GRANTS: ReadonlyMap<string, string> = new Map([
    ['enterprise', 'the enterprise-wide grant'],
    ['team', 'the team’s grant'],
    ['role', 'the role’s grant'],
    ['member', 'the member’s grant'],
])

/** The API's refusal for a site a wider grant leaves out is `widens_<parentLayer>_grant:<host>,<host>`. */
const WIDENS = 'widens_'
const GRANT = '_grant'

/** `a`, `a and b`, `a, b and c`. */
function listOf(items: readonly string[]): string {
    const rest = items.slice(0, -1)
    return rest.length ? `${rest.join(', ')} and ${items[items.length - 1]}` : items.join('')
}

/**
 * The copy for a lower layer's Computer-Use save that adds a site a wider grant leaves out: the refused sites, the
 * wider grant in words, and what to do next, like the other refusals here: remove them and save again, or ask who
 * can add them (the owner check the API makes, or whoever bound that grant). Null for a code it cannot fully read
 * (an unknown layer, no hosts, an empty host), so the screen keeps its generic text and never shows the raw code.
 */
function wideningRefusalMessage(reason: string): string | null {
    const colon = reason.indexOf(':')
    const code = reason.slice(0, colon)
    if (colon < 0 || !code.startsWith(WIDENS) || !code.endsWith(GRANT)) return null
    const grant = WIDER_GRANTS.get(code.slice(WIDENS.length, -GRANT.length))
    const hosts = reason.slice(colon + 1).split(',')
    if (!grant || hosts.includes('')) return null
    const [verb, them] = hosts.length === 1 ? ['is', 'it'] : ['are', 'them']
    return `${listOf(hosts)} ${verb} outside ${grant}, so a binding here cannot add ${them}. Remove ${them} and save again, or ask the owner of this enterprise, a member the owner has allowed, or whoever bound that grant to add ${them}.`
}

/**
 * The copy for the reason a save was refused with, or null when it has none of its own. A reason that names the value
 * it refused (`tool_slot_key_invalid:<key>`) is read by the part before the colon. A widening refusal carries its
 * meaning inside the code (`widens_team_grant:a.example.com`), so its copy is built from the layer and the sites.
 */
export function bindRefusalMessage(error: unknown): string | null {
    if (!(error instanceof Error)) return null
    return (
        REFUSALS.get(error.message) ??
        REFUSALS.get(error.message.split(':')[0] ?? '') ??
        wideningRefusalMessage(error.message)
    )
}

/**
 * Revoking a saved sign-in takes the same access as changing its binding, so it is refused with the same reasons. The
 * enterprise-layer one says what was refused: revoking the sign-in, not changing the tool.
 */
const REVOKE_REFUSALS: ReadonlyMap<string, string> = new Map([
    [
        'ceo_bind_only',
        'Only the owner of this enterprise, or a member the owner has allowed, can revoke a sign-in saved on an enterprise-wide tool.',
    ],
])

/** The copy for the reason a saved sign-in's revoke was refused with, or null when it has none of its own. */
export function revokeRefusalMessage(error: unknown): string | null {
    return error instanceof Error ? (REVOKE_REFUSALS.get(error.message) ?? bindRefusalMessage(error)) : null
}
